import { NSARouterMakerResult } from "namirasoft-account-react";
import { SortItem } from "namirasoft-core";
import { NSBaseMetaTable } from "namirasoft-site";
import { NamirasoftMap } from "namirasoft-site-map";
import { IBaseComponentProps, NSBox, NSBoxEntity, NSBoxEnum, NSInfoProps, NSMenuButtonMenuProps } from "namirasoft-site-react";
import { Component, createRef, Fragment } from "react";
import { InferenceRouterProps } from "../InferenceRouterProps";

interface InferencerEntityRow
{
    id: string;
    name: string;
}

export interface InferencerTypeIDValue<TEnum extends string>
{
    inferencer_type: TEnum;
    inferencer_id: string;
}

export interface InferencerTypeIDProps<TEnum extends string> extends IBaseComponentProps, NSARouterMakerResult, InferenceRouterProps
{
    typeTitle: string;
    idTitle: string | ((type: TEnum) => string);
    required?: boolean;
    reloadable?: boolean;
    width?: string;
    fill?: boolean;
    getEnumObject: () => { [s: string]: TEnum };
    defaultType: TEnum;
    getTable?: (type: TEnum, namirasoft: NamirasoftMap) => NSBaseMetaTable<any, any>;
    onChanged?: () => void;
    info?: NSInfoProps;
    menu?: NSMenuButtonMenuProps;
    typeClassList?: string[];
    idClassList?: string[];
}

interface InferencerTypeIDState<TEnum extends string>
{
    inferencer_type: TEnum;
}

export default class InferencerTypeID<TEnum extends string> extends Component<InferencerTypeIDProps<TEnum>, InferencerTypeIDState<TEnum>>
{
    private NSBoxEnum_Type = createRef<NSBoxEnum<TEnum>>();
    private NSBoxEntity_Id = createRef<NSBoxEntity<InferencerEntityRow>>();

    constructor(props: InferencerTypeIDProps<TEnum>)
    {
        super(props);
        this.state = { inferencer_type: props.defaultType };
        this.getValue = this.getValue.bind(this);
        this.setValue = this.setValue.bind(this);
        this.onTypeChanged = this.onTypeChanged.bind(this);
        this.onIdChanged = this.onIdChanged.bind(this);
    }

    getValue(checkError: boolean = false): InferencerTypeIDValue<TEnum>
    {
        const inferencer_type = (this.NSBoxEnum_Type.current?.getValueEnumOne(this.props.getEnumObject(), this.props.defaultType, checkError) ?? this.props.defaultType) as TEnum;
        const inferencer_id = this.NSBoxEntity_Id.current?.getValueOne(checkError) ?? "";
        return { inferencer_type, inferencer_id };
    }

    setValue(value: InferencerTypeIDValue<TEnum>): void
    {
        this.setState({ inferencer_type: value.inferencer_type }, () =>
        {
            this.NSBoxEnum_Type.current?.setValue(value.inferencer_type as unknown as string);
            this.NSBoxEntity_Id.current?.setValue(value.inferencer_id);
        });
    }

    private resolveTable(namirasoft: NamirasoftMap): NSBaseMetaTable<any, any>
    {
        if (this.props.getTable)
            return this.props.getTable(this.state.inferencer_type, namirasoft);
        const key = String(this.state.inferencer_type).toLowerCase();
        return (namirasoft.inference.tables as any)[key];
    }

    private onTypeChanged(): void
    {
        const next = this.NSBoxEnum_Type.current?.getValueEnumOne(this.props.getEnumObject(), this.props.defaultType, false) ?? this.props.defaultType;
        if (next !== this.state.inferencer_type)
            this.setState({ inferencer_type: next as TEnum }, () =>
            {
                this.NSBoxEntity_Id.current?.setValue(null);
                this.props.onChanged?.();
            });
        else
            this.props.onChanged?.();
    }

    private onIdChanged(): void
    {
        this.props.onChanged?.();
    }

    override render()
    {
        const namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);
        const table = this.resolveTable(namirasoft);
        const required = this.props.required ?? true;
        const width = this.props.width ?? NSBox.width.one;
        const boxStyle = this.props.fill
            ? { flex: 1, minWidth: 0 }
            : { width, minWidth: width, flexShrink: 0 };
        const idTitle = typeof this.props.idTitle === "function"
            ? this.props.idTitle(this.state.inferencer_type)
            : this.props.idTitle;
        return (
            <Fragment>
                <NSBoxEnum<TEnum>
                    ref={this.NSBoxEnum_Type}
                    title={this.props.typeTitle}
                    required={required}
                    multiple={false}
                    reloadable={this.props.reloadable ?? false}
                    getEnumObject={this.props.getEnumObject}
                    onChanged={this.onTypeChanged}
                    style={boxStyle}
                    info={this.props.info}
                    classList={this.props.typeClassList}
                />
                <NSBoxEntity<InferencerEntityRow>
                    ref={this.NSBoxEntity_Id}
                    key={`inferencer-id-${String(this.state.inferencer_type)}`}
                    title={idTitle}
                    required={required}
                    multiple={false}
                    table={table}
                    getValue={(item) => item.id}
                    getTitle={(item) => item.name}
                    getSort={() => new SortItem(table, (table as any).columns.name, true)}
                    onChanged={this.onIdChanged}
                    style={boxStyle}
                    info={this.props.info}
                    menu={this.props.menu}
                    classList={this.props.idClassList}
                />
            </Fragment>
        );
    }
}